import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
  type FC,
} from "react";

import { AppStrings } from "../../../../utils/constants/AppStrings";

import {
  buildColorCssVariables,
  buildGamePassColorRows,
  resolveGamePassPalette,
  type GamePassColorRow,
} from "../ColorSection/gamePassColors";
import { type AssetFiles } from "../AssetsSection/AssetsSection";
import { type AssetKey } from "../AssetsSection/assetsSectionData";

import {
  GAME_PASS_PREVIEW_IMAGES,
  resolvePreviewImage,
} from "./gamePassPreviewAssets";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  BackIcon,
  ClockIcon,
  GiftIcon,
  InfoIcon,
  TickIcon,
} from "./gamePassPreviewIcons";

import styles from "./GamePassPreview.module.css";

export type GamePassPreviewProps = {
  /** Colour rows (defaults to the trivelta palette) used to map the editable
   *  swatches onto the preview's `--COLOR-*` overrides. */
  rows?: GamePassColorRow[];
  /** Editable colours map from the Color section (keyed by `${key}-${index}`). */
  colors?: Record<string, string>;
  /** Uploaded asset files from the Assets section, overriding the defaults. */
  assetFiles?: AssetFiles;
  /** Default asset URLs resolved from TCM (see `gamePassDefaultAssets`),
   *  shown for slots the admin hasn't uploaded to. */
  defaultAssetUrls?: Partial<Record<AssetKey, string>>;
};

const EMPTY_COLORS: Record<string, string> = {};
const EMPTY_ASSETS: AssetFiles = {};

/** Creates (and revokes) object URLs for the uploaded asset files so the
 *  preview can render them, mirroring the Assets section's tile previews. */
const useAssetObjectUrls = (
  assetFiles: AssetFiles,
): Partial<Record<AssetKey, string>> => {
  const [objectUrls, setObjectUrls] = useState<
    Partial<Record<AssetKey, string>>
  >({});

  useEffect(() => {
    const urls: Partial<Record<AssetKey, string>> = {};
    (Object.keys(assetFiles) as AssetKey[]).forEach((key) => {
      const file = assetFiles[key];
      if (file) urls[key] = URL.createObjectURL(file);
    });
    setObjectUrls(urls);
    return () =>
      Object.values(urls).forEach((url) => url && URL.revokeObjectURL(url));
  }, [assetFiles]);

  return objectUrls;
};

/**
 * Game Pass → Style → live preview (right-hand mobile view).
 *
 * A representative, in-app mock of the trivelta-gamepass-frontend season screen
 * (header → season header → challenge ladder → task rows → rewards / claim),
 * laid out 1:1 with that design. It is driven entirely by the editors on the
 * left: every surface reads its colours from the `--COLOR-*` variables the
 * Color section emits (applied inline to this component's root so untouched
 * tokens fall through to the platform palette), and the cash / coin /
 * challenge-complete icons swap to the admin's uploads from the Assets section
 * the moment they're picked. The task-row thumbnails stay on the static
 * challenge badge — those are per-task images in the real app, not themeable
 * brand assets. The numbers and titles are illustrative placeholders, not
 * live data.
 */
const GamePassPreview: FC<GamePassPreviewProps> = ({
  rows: rowsProp,
  colors = EMPTY_COLORS,
  assetFiles = EMPTY_ASSETS,
  defaultAssetUrls,
}) => {
  const internalRows = useMemo(
    () => buildGamePassColorRows(resolveGamePassPalette()),
    [],
  );
  const rows = rowsProp ?? internalRows;

  const objectUrls = useAssetObjectUrls(assetFiles);

  const previewImage = useCallback(
    (key: AssetKey): string =>
      resolvePreviewImage(key, objectUrls, defaultAssetUrls),
    [objectUrls, defaultAssetUrls],
  );

  const colorVars = useMemo(
    () =>
      ({
        ...buildColorCssVariables(rows, colors),
        // PUBLIC_URL-aware texture path fed to the CSS background layers (kept
        // here rather than hardcoded in the stylesheet so it honours the app's
        // public base). Driven by the editable Carbon Fibre Background slot so
        // an admin's uploaded (or TCM-default) page texture updates the preview
        // live; falls back to the bundled trivelta texture until one is set.
        "--gp-page-texture": `url("${previewImage("carbonFibreBackground")}")`,
      }) as CSSProperties,
    [rows, colors, previewImage],
  );

  return (
    <aside
      className={styles.card}
      data-testid="game-pass-style-preview"
      style={colorVars}
    >
      <div className={styles.phone}>
        <div className={styles.screen}>
          <header className={styles.appHeader}>
            <button
              type="button"
              className={styles.iconButton}
              aria-label={AppStrings.GAME_PASS_STYLE_PREVIEW_BACK_ARIA}
            >
              <BackIcon />
            </button>
            <h2 className={styles.appTitle}>
              {AppStrings.GAME_PASS_STYLE_PREVIEW_TITLE}
            </h2>
            <button
              type="button"
              className={styles.iconButton}
              aria-label={AppStrings.GAME_PASS_STYLE_PREVIEW_INFO_ARIA}
            >
              <InfoIcon />
            </button>
          </header>

          <section className={styles.seasonHeader}>
            <div className={styles.seasonTitleRow}>
              <span className={styles.seasonTitle}>
                {AppStrings.GAME_PASS_STYLE_PREVIEW_SEASON}
              </span>
              <span className={styles.countdownPill}>
                <ClockIcon className={styles.clockIcon} />
                {AppStrings.GAME_PASS_STYLE_PREVIEW_COUNTDOWN}
              </span>
            </div>

            <div className={styles.statsCard}>
              <div className={styles.statsColumn}>
                <div className={styles.statLine}>
                  <span className={styles.statLabel}>
                    {AppStrings.GAME_PASS_STYLE_PREVIEW_NEXT_POSITION}
                  </span>
                  <span className={styles.statValueGroup}>
                    <span className={styles.statValue}>
                      {AppStrings.GAME_PASS_STYLE_PREVIEW_NEXT_XP}
                    </span>
                    <ArrowUpIcon className={styles.arrowUp} />
                  </span>
                </div>
                <div className={styles.statLine}>
                  <span className={styles.statLabel}>
                    {AppStrings.GAME_PASS_STYLE_PREVIEW_PREVIOUS_POSITION}
                  </span>
                  <span className={styles.statValueGroup}>
                    <span className={styles.statValue}>
                      {AppStrings.GAME_PASS_STYLE_PREVIEW_PREVIOUS_XP}
                    </span>
                    <ArrowDownIcon className={styles.arrowDown} />
                  </span>
                </div>
              </div>

              <div className={styles.progressBlock}>
                <span className={styles.progressLabel}>
                  {AppStrings.PROGRESS}
                </span>
                <div className={styles.statsRing}>
                  <span className={styles.ringNumerator}>
                    {AppStrings.GAME_PASS_STYLE_PREVIEW_PROGRESS_CURRENT}
                  </span>
                  <span className={styles.ringSeparator}>
                    {AppStrings.GAME_PASS_STYLE_PREVIEW_PROGRESS_SEPARATOR}
                  </span>
                  <span className={styles.ringDenominator}>
                    {AppStrings.GAME_PASS_STYLE_PREVIEW_PROGRESS_TOTAL}
                  </span>
                </div>
              </div>
            </div>
          </section>

          <section className={styles.ladder}>
            <div className={styles.rail}>
              <div className={styles.railBadge}>
                <img
                  className={styles.railBadgeImage}
                  src={GAME_PASS_PREVIEW_IMAGES.challengeBadge}
                  alt={AppStrings.GAME_PASS_STYLE_PREVIEW_BADGE_ALT}
                />
                <span className={styles.orderBadge}>
                  <span className={styles.orderBadgeInner}>
                    {AppStrings.GAME_PASS_STYLE_PREVIEW_CHALLENGE_NUMBER}
                  </span>
                </span>
              </div>

              <div className={styles.railTrack}>
                <div className={styles.railFill} />
                <span className={styles.railXp}>
                  {AppStrings.GAME_PASS_STYLE_PREVIEW_RAIL_XP}
                </span>
              </div>

              <img
                className={styles.railTrophy}
                src={previewImage("challengeComplete")}
                alt={AppStrings.GAME_PASS_STYLE_PREVIEW_TROPHY_ALT}
              />
            </div>

            <div className={styles.challenge}>
              <div className={styles.hero}>
                <div className={styles.heroBadge}>
                  <img
                    className={styles.heroBadgeImage}
                    src={GAME_PASS_PREVIEW_IMAGES.challengeBadge}
                    alt={AppStrings.GAME_PASS_STYLE_PREVIEW_BADGE_ALT}
                  />
                  <span className={styles.orderBadge}>
                    <span className={styles.orderBadgeInner}>
                      {AppStrings.GAME_PASS_STYLE_PREVIEW_CHALLENGE_NUMBER}
                    </span>
                  </span>
                </div>
                <div className={styles.heroText}>
                  <h3 className={styles.challengeTitle}>
                    {AppStrings.GAME_PASS_STYLE_PREVIEW_CHALLENGE_TITLE}
                  </h3>
                  <p className={styles.challengeDescription}>
                    {AppStrings.GAME_PASS_STYLE_PREVIEW_CHALLENGE_DESCRIPTION}
                  </p>
                </div>
              </div>

              <div className={styles.taskList}>
                <div className={`${styles.taskRow} ${styles.taskRowCompleted}`}>
                  <div className={styles.taskImage}>
                    <img
                      src={GAME_PASS_PREVIEW_IMAGES.challengeBadge}
                      alt={AppStrings.GAME_PASS_STYLE_PREVIEW_TASK_ICON_ALT}
                    />
                  </div>
                  <div className={styles.taskContent}>
                    <span
                      className={`${styles.xpPill} ${styles.xpPillCompleted}`}
                    >
                      {AppStrings.GAME_PASS_STYLE_PREVIEW_TASK_XP_COMPLETED}
                    </span>
                    <div className={styles.taskBottom}>
                      <div className={styles.taskTextColumn}>
                        <span className={styles.taskTitle}>
                          {AppStrings.GAME_PASS_STYLE_PREVIEW_TASK_TITLE}
                        </span>
                        <span className={styles.statusRow}>
                          <TickIcon className={styles.statusTick} />
                          <span className={styles.statusText}>
                            {AppStrings.COMPLETED}
                          </span>
                        </span>
                      </div>
                      <div className={styles.completedRing}>
                        <TickIcon className={styles.completedRingTick} />
                      </div>
                    </div>
                  </div>
                </div>

                <div className={styles.taskRow}>
                  <div className={styles.taskImage}>
                    <img
                      src={GAME_PASS_PREVIEW_IMAGES.challengeBadge}
                      alt={AppStrings.GAME_PASS_STYLE_PREVIEW_TASK_ICON_ALT}
                    />
                  </div>
                  <div className={styles.taskContent}>
                    <span className={styles.xpPill}>
                      {AppStrings.GAME_PASS_STYLE_PREVIEW_TASK_XP}
                    </span>
                    <div className={styles.taskBottom}>
                      <div className={styles.taskTextColumn}>
                        <span className={styles.taskTitle}>
                          {AppStrings.GAME_PASS_STYLE_PREVIEW_TASK_TITLE}
                        </span>
                        <span className={styles.taskSubtitle}>
                          {
                            AppStrings.GAME_PASS_STYLE_PREVIEW_TASK_DESCRIPTION
                          }
                        </span>
                      </div>
                      <div className={styles.progressRing}>
                        <span className={styles.ringNumerator}>
                          {
                            AppStrings.GAME_PASS_STYLE_PREVIEW_TASK_PROGRESS_CURRENT
                          }
                        </span>
                        <span className={styles.ringSeparator}>
                          {
                            AppStrings.GAME_PASS_STYLE_PREVIEW_PROGRESS_SEPARATOR
                          }
                        </span>
                        <span className={styles.ringDenominator}>
                          {
                            AppStrings.GAME_PASS_STYLE_PREVIEW_TASK_PROGRESS_TOTAL
                          }
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles.rewardsCard}>
                <div className={styles.rewardsHeader}>
                  <span className={styles.rewardsTitle}>
                    {AppStrings.GAME_PASS_STYLE_PREVIEW_REWARDS}
                  </span>
                  <span className={styles.rewardsTrophy} aria-hidden>
                    <img
                      src={previewImage("challengeComplete")}
                      alt={AppStrings.GAME_PASS_STYLE_PREVIEW_TROPHY_ALT}
                    />
                  </span>
                </div>
                <div className={styles.rewardsTiles}>
                  <div className={styles.rewardsTile}>
                    <img
                      className={styles.rewardIcon}
                      src={previewImage("cash")}
                      alt={AppStrings.GAME_PASS_STYLE_PREVIEW_CASH_ALT}
                    />
                    <span className={styles.rewardValue}>
                      {AppStrings.GAME_PASS_STYLE_PREVIEW_REWARD_CASH}
                    </span>
                  </div>
                  <div className={styles.rewardsTile}>
                    <img
                      className={styles.rewardIcon}
                      src={previewImage("coins")}
                      alt={AppStrings.GAME_PASS_STYLE_PREVIEW_COINS_ALT}
                    />
                    <span className={styles.rewardValue}>
                      {AppStrings.GAME_PASS_STYLE_PREVIEW_REWARD_COINS}
                    </span>
                  </div>
                </div>
              </div>

              <p className={styles.xpLeft}>
                <span className={styles.xpLeftAmount}>
                  {AppStrings.GAME_PASS_STYLE_PREVIEW_XP_LEFT_AMOUNT}
                </span>{" "}
                {AppStrings.GAME_PASS_STYLE_PREVIEW_XP_LEFT_SUFFIX}
              </p>

              <button type="button" className={styles.claimButton}>
                <GiftIcon className={styles.giftIcon} />
                <span className={styles.claimButtonText}>
                  {AppStrings.GAME_PASS_STYLE_PREVIEW_CLAIM_REWARDS}
                </span>
              </button>
            </div>
          </section>
        </div>
      </div>
    </aside>
  );
};

export default GamePassPreview;
